'use client';

import React, { useState } from 'react';

interface Payout {
  id: string;
  amount: number;
  method: string;
  account: string;
  requestedOn: string;
  processedOn: string | null;
  status: 'completed' | 'processing' | 'pending' | 'failed';
  period: string;
}

const payouts: Payout[] = [
  {
    id: 'PAY-0041',
    amount: 1800000,
    method: 'MTN Mobile Money',
    account: '+256 7XX XXX 001',
    requestedOn: '2026-08-01',
    processedOn: '2026-08-03',
    status: 'completed',
    period: 'Jul 2026',
  },
  {
    id: 'PAY-0042',
    amount: 1200000,
    method: 'Airtel Money',
    account: '+256 7XX XXX 001',
    requestedOn: '2026-08-15',
    processedOn: null,
    status: 'processing',
    period: 'Aug 1–15, 2026',
  },
  {
    id: 'PAY-0043',
    amount: 600000,
    method: 'Bank Transfer',
    account: 'Stanbic Bank ****4521',
    requestedOn: '2026-07-01',
    processedOn: '2026-07-04',
    status: 'completed',
    period: 'Jun 2026',
  },
  {
    id: 'PAY-0044',
    amount: 950000,
    method: 'MTN Mobile Money',
    account: '+256 7XX XXX 001',
    requestedOn: '2026-06-01',
    processedOn: '2026-06-02',
    status: 'completed',
    period: 'May 2026',
  },
];

const statusConfig = {
  completed: { color: 'bg-emerald-100 text-emerald-700', label: 'Paid Out' },
  processing: { color: 'bg-blue-100 text-blue-700', label: 'Processing' },
  pending: { color: 'bg-amber-100 text-amber-700', label: 'Pending' },
  failed: { color: 'bg-red-100 text-red-600', label: 'Failed' },
};

const methodIcons: Record<string, string> = {
  'MTN Mobile Money': '📱',
  'Airtel Money': '📲',
  'Bank Transfer': '🏦',
};

export default function PayoutRequests() {
  const [showModal, setShowModal] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('MTN Mobile Money');
  const [payoutAmount, setPayoutAmount] = useState('');

  const availableBalance = 1800000;

  return (
    <div>
      {/* Balance cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Available Balance', value: 'UGX 1.8M', sub: 'Ready to withdraw', highlight: true },
          { label: 'Processing', value: 'UGX 1.2M', sub: 'Est. 2–3 business days', highlight: false },
          { label: 'Total Paid Out', value: 'UGX 4.55M', sub: 'All time', highlight: false },
          { label: 'Next Payout', value: 'Sep 1, 2026', sub: 'Automatic schedule', highlight: false },
        ].map((s) => (
          <div key={s.label} className={`rounded-2xl p-5 border shadow-sm ${s.highlight ? 'bg-rose-500 border-rose-400 text-white' : 'bg-white border-gray-100'}`}>
            <div className={`text-xl font-bold ${s.highlight ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
            <div className={`text-sm font-semibold mt-0.5 ${s.highlight ? 'text-rose-100' : 'text-gray-700'}`}>{s.label}</div>
            <div className={`text-xs mt-1 ${s.highlight ? 'text-rose-200' : 'text-gray-400'}`}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Request payout button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">Payout History</h3>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
        >
          💳 Request Payout
        </button>
      </div>

      {/* Payout table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Payout ID</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Period</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Method</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Amount</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Requested</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Processed</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-sm font-mono text-gray-700">{p.id}</td>
                  <td className="px-5 py-4 text-sm text-gray-700">{p.period}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span>{methodIcons[p.method] || '💰'}</span>
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{p.method}</div>
                        <div className="text-xs text-gray-400">{p.account}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm font-bold text-gray-900">UGX {p.amount.toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm text-gray-600">{p.requestedOn}</td>
                  <td className="px-5 py-4 text-sm text-gray-600">{p.processedOn || '—'}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusConfig[p.status].color}`}>
                      {statusConfig[p.status].label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout methods */}
      <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-900 mb-4">Payout Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { icon: '📱', name: 'MTN Mobile Money', account: '+256 7XX XXX 001', primary: true },
            { icon: '📲', name: 'Airtel Money', account: '+256 7XX XXX 001', primary: false },
            { icon: '🏦', name: 'Stanbic Bank', account: '****4521', primary: false },
          ].map((m) => (
            <div key={m.name} className={`flex items-center gap-3 p-4 rounded-xl border-2 ${m.primary ? 'border-rose-400 bg-rose-50' : 'border-gray-100'}`}>
              <span className="text-2xl">{m.icon}</span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-800">{m.name}</div>
                <div className="text-xs text-gray-500">{m.account}</div>
              </div>
              {m.primary && <span className="text-xs bg-rose-500 text-white px-2 py-0.5 rounded-full font-bold">Primary</span>}
            </div>
          ))}
        </div>
        <button className="mt-3 text-sm text-rose-500 font-semibold hover:underline">+ Add payout method</button>
      </div>

      {/* Request Payout Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Request Payout</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-5">
              <div className="text-xs text-gray-500">Available Balance</div>
              <div className="text-2xl font-bold text-gray-900">UGX {availableBalance.toLocaleString()}</div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Amount (UGX)</label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Payout Method</label>
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                >
                  <option>MTN Mobile Money</option>
                  <option>Airtel Money</option>
                  <option>Bank Transfer</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
              >
                Request Payout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
